import React, { Component, type ComponentType } from 'react';
import * as R from 'ramda';
import type { TableHeaderRenderer } from 'react-virtualized';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ArrowRightIconOld from '@atlaskit/icon/glyph/arrow-right';
import { Box, xcss } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import {
	AutoSizer,
	Column,
	Table,
	type TableCellRenderer,
} from '@atlassian/jira-portfolio-3-common/src/react-virtualized';
import ScrollLongTaskAnalytics from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/scroll-long-task-analytics';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import type {
	Change,
	ScenarioType,
	TeamAndResourceDetails,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/update-jira/types';
import { getIdForChangeCategory } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/title-bar/update-jira/utils';
import {
	ENTITY,
	SCENARIO_TYPE,
	SCHEDULE_MODE,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import WarningDetails from '@atlassian/jira-portfolio-3-portfolio/src/common/warning-details';
import { ROW_HEIGHT } from './constants';
import LastModified from './last-modified';
import messages from './messages';
import SelectChangeCell from './select-change/cell';
import SelectChangeHeader from './select-change/header';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import CrossProjectReleaseTitleCell from './title/cross-project-release';
import IssueTitleCell from './title/issue';
import ReleaseTitleCell from './title/release';
import SprintTitle from './title/sprint';
import TeamTitleCell from './title/team';
import type { Props } from './types';
import ValueChange from './value-change';
import { VALUE_TYPE } from './value-change/types';
import WhatChanged from './what-changed';

// eslint-disable-next-line jira/react/no-class-components
class ChangesTable extends Component<Props & { intl: IntlShape }> {
	static defaultProps = {
		commitWarningsExists: false,
		ValueChangeComponent: ValueChange,
		WhatChangedComponent: WhatChanged,
		SelectChangeHeader,
		SelectChangeCell,
		IssueTitleCell,
	};

	selectColumnHeaderRenderer = () => {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { SelectChangeHeader, changes, isUpdating } = this.props;
		return <SelectChangeHeader changes={changes} isUpdating={isUpdating} />;
	};

	selectCellRenderer: TableCellRenderer = ({ rowData }) => {
		const { id, category, type } = rowData;
		if (type === SCENARIO_TYPE.NONE) return null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let items: any[] = [{ id, category }];

		if (
			(category === ENTITY.TEAM || category === ENTITY.RESOURCE) &&
			rowData.details.changes.length > 0
		) {
			items = [];
			const uniqIds = new Set();
			const addItem = (
				item:
					| {
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							category: any;
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							id: any;
					  }
					| {
							category: never;
							id: never;
					  },
			) => {
				const uniqId = `${item.category}:${item.id}`;
				if (!uniqIds.has(uniqId)) {
					uniqIds.add(uniqId);
					items.push(item);
				}
			};

			const changes = rowData.details.changes;
			for (const change of changes) {
				if (change.category === ENTITY.RESOURCE) {
					addItem({
						id: change.resourceId,
						category: change.category,
					});
				} else {
					addItem({
						id,
						category,
					});
				}
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { isUpdating, SelectChangeCell } = this.props;
		return (
			<SelectChangeCell
				id={getIdForChangeCategory(id, category)}
				items={items}
				isUpdating={isUpdating}
			/>
		);
	};

	titleCellRenderer: TableCellRenderer = ({ rowData }) => {
		const change: Change = rowData;
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { IssueTitleCell, useTwpTeams = false } = this.props;

		if (change.type !== SCENARIO_TYPE.NONE) {
			switch (change.category) {
				case ENTITY.ISSUE:
					return <IssueTitleCell details={change.details} />;
				case ENTITY.RELEASE:
					return <ReleaseTitleCell details={change.details} />;
				case ENTITY.CROSS_PROJECT_RELEASE:
					return <CrossProjectReleaseTitleCell crossProjectVersion={change.details.values} />;
				case ENTITY.TEAM:
				case ENTITY.RESOURCE: {
					const teamDetail: TeamAndResourceDetails = change.details;
					return (
						<TeamTitleCell
							name={teamDetail.title.name}
							avatarUrl={teamDetail.title.avatarUrl}
							useTwpTeams={useTwpTeams}
							id={change.id}
							category={change.category}
						/>
					);
				}
				case ENTITY.SPRINT:
					return (
						<SprintTitle sprint={change.details.values} id={change.id} category={change.category} />
					);
				default:
					return <span />;
			}
		}
		return <span />;
	};

	categoryCellRenderer: TableCellRenderer = ({ cellData, rowData }) => {
		const { intl } = this.props;
		const { type } = rowData;
		if (type !== SCENARIO_TYPE.NONE) {
			switch (cellData) {
				case ENTITY.ISSUE:
					return <span>{intl.formatMessage(messages.issueCategory)}</span>;
				case ENTITY.TEAM:
				case ENTITY.RESOURCE:
					return <span>{intl.formatMessage(messages.teamCategory)}</span>;
				case ENTITY.RELEASE:
					return <span>{intl.formatMessage(messages.releaseCategory)}</span>;
				case ENTITY.CROSS_PROJECT_RELEASE:
					return <span>{intl.formatMessage(commonMessages.crossProjectRelease)}</span>;
				case ENTITY.SPRINT: {
					const schedulingMode = R.path(['details', 'values', 'schedulingMode'], rowData);
					return (
						<span>
							{intl.formatMessage(
								schedulingMode === SCHEDULE_MODE.kanban
									? messages.iterationCategory
									: messages.sprintCategory,
							)}
						</span>
					);
				}
				default:
					return null;
			}
		}
		return null;
	};

	whatChangedCellRenderer: TableCellRenderer = ({ cellData, rowData }) => {
		const { WhatChangedComponent } = this.props;
		const scenarioType: ScenarioType = cellData;
		const { attributeName, changeCount, id, category } = rowData;
		return (
			<WhatChangedComponent
				attributeName={attributeName}
				changeType={scenarioType}
				changeCount={changeCount}
				id={id}
				category={category}
				changeDetails={rowData}
			/>
		);
	};

	commitWarningCellRenderer: TableCellRenderer = ({ cellData }) => {
		if (R.isEmpty(cellData)) {
			return null;
		}

		return <WarningDetails warnings={cellData} />;
	};

	valueCellRenderer: TableCellRenderer = ({ dataKey, rowData }) => {
		const { ValueChangeComponent } = this.props;

		return <ValueChangeComponent change={rowData} valueType={dataKey} />;
	};

	lastModifiedCellRenderer: TableCellRenderer = ({
		cellData: { date, title, personId, avatarUrl, dateFormat } = {},
	}) => {
		if (!personId) {
			return null;
		}

		return (
			<LastModified
				date={date}
				title={title}
				personId={personId}
				avatarUrl={avatarUrl}
				dateFormat={dateFormat}
			/>
		);
	};

	lastModifiedColumnHeaderRenderer = () => {
		const { intl, sortDirection } = this.props;
		return ff('plans-update-review-changes-to-unsaved-changes_sbz4w') ? (
			<Box xcss={lastModifiedHeaderStyles}>
				<Box as="span">{intl.formatMessage(messages.lastModifiedColumnHeader)}</Box>
				<Box xcss={lastModifiedHeaderArrowStyles}>
					{sortDirection === 'ASC' ? (
						<ArrowUpIcon label="Sort ascending" />
					) : (
						<ArrowDownIcon label="Sort descending" />
					)}
				</Box>
			</Box>
		) : (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.modifiedColumnHeader}>
				<span data-sortdir={sortDirection}>
					{intl.formatMessage(messages.lastModifiedColumnHeader)}
				</span>
			</div>
		);
	};

	oldValueColumnHeaderRenderer = () => {
		const { intl } = this.props;
		return ff('plans-update-review-changes-to-unsaved-changes_sbz4w') ? (
			<Box xcss={columnHeaderStyles}>
				<Box as="span">{intl.formatMessage(messages.oldValueColumnHeader)}</Box>
				<Box as="span" xcss={columnHeaderArrowStyles}>
					<ArrowRightIcon label="" />
				</Box>
			</Box>
		) : (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.oldColumnHeader}>
				<span>{intl.formatMessage(messages.oldValueColumnHeader)}</span>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<span className={styles.oldColumnHeaderArrow}>
					<ArrowRightIconOld label="" size="small" />
				</span>
			</div>
		);
	};

	tableHeaderRenderer: TableHeaderRenderer = ({ label }) => label;

	sort = () => {
		const { sortChanges, sortDirection, sortBy } = this.props;
		sortChanges(sortBy, sortDirection === 'ASC' ? 'DESC' : 'ASC');
	};

	render() {
		const { changes, commitWarningsExists, intl, isReadOnly, handleScroll, sortDirection, sortBy } =
			this.props;
		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styles.container}
				data-name="update-jira-dialog-content"
				data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.update-jira.changes-table"
				aria-label={intl.formatMessage(messages.tableAriaLabel)}
			>
				<AutoSizer>
					{({ width, height }) => (
						<Table
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={
								ff('com.atlassian.rm.jpo.jpo3cloud.centralise-react-virtualized-overflow-styles')
									? 'changesTable'
									: styles.table
							}
							gridClassName={styles.grid}
							width={width}
							height={height}
							headerHeight={20}
							headerClassName={styles.columnHeader}
							rowHeight={ROW_HEIGHT}
							rowCount={changes.length}
							rowGetter={({ index }) => changes[index]}
							rowClassName={styles.tableRow}
							sort={this.sort}
							sortDirection={sortDirection}
							sortBy={sortBy}
							onScroll={handleScroll}
						>
							{commitWarningsExists && (
								<Column
									dataKey="warnings"
									width={25}
									cellRenderer={this.commitWarningCellRenderer}
									disableSort
								/>
							)}
							{!isReadOnly && (
								<Column
									label={this.selectColumnHeaderRenderer()}
									dataKey="isSelected"
									width={30}
									cellRenderer={this.selectCellRenderer}
									disableSort
									{...(ff('plans-update-review-changes-to-unsaved-changes_sbz4w')
										? { headerRenderer: this.tableHeaderRenderer }
										: {})}
								/>
							)}
							<Column
								label={intl.formatMessage(messages.titleColumnHeader)}
								dataKey="details"
								width={100}
								cellRenderer={this.titleCellRenderer}
								flexGrow={1}
								disableSort
								{...(ff('plans-update-review-changes-to-unsaved-changes_sbz4w')
									? { headerRenderer: this.tableHeaderRenderer }
									: {})}
							/>
							<Column
								width={150}
								label={intl.formatMessage(messages.categoryColumnHeader)}
								dataKey="category"
								cellRenderer={this.categoryCellRenderer}
								disableSort
								{...(ff('plans-update-review-changes-to-unsaved-changes_sbz4w')
									? { headerRenderer: this.tableHeaderRenderer }
									: {})}
							/>
							<Column
								width={250}
								label={intl.formatMessage(messages.whatChangedColumnHeader)}
								dataKey="type"
								cellRenderer={this.whatChangedCellRenderer}
								disableSort
								{...(ff('plans-update-review-changes-to-unsaved-changes_sbz4w')
									? { headerRenderer: this.tableHeaderRenderer }
									: {})}
							/>
							<Column
								width={150}
								dataKey={VALUE_TYPE.CURRENT}
								cellRenderer={this.valueCellRenderer}
								headerRenderer={this.oldValueColumnHeaderRenderer}
								disableSort
							/>
							<Column
								width={150}
								label={intl.formatMessage(commonMessages.new)}
								dataKey={VALUE_TYPE.NEW}
								cellRenderer={this.valueCellRenderer}
								disableSort
								{...(ff('plans-update-review-changes-to-unsaved-changes_sbz4w')
									? { headerRenderer: this.tableHeaderRenderer }
									: {})}
							/>
							<Column
								width={150}
								label={intl.formatMessage(messages.lastModifiedColumnHeader)}
								dataKey="lastModified"
								headerRenderer={this.lastModifiedColumnHeaderRenderer}
								cellRenderer={this.lastModifiedCellRenderer}
							/>
						</Table>
					)}
				</AutoSizer>
			</div>
		);
	}
}

export default injectIntl(ChangesTable);

const lastModifiedHeaderStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
	outline: 'none',
	gap: 'space.050',
});

const lastModifiedHeaderArrowStyles = xcss({
	display: 'flex',
	alignSelf: 'center',
});

const columnHeaderStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

const columnHeaderArrowStyles = xcss({
	display: 'flex',
	marginRight: 'space.075',
});

export const withScrollTracking =
	<C extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
		WrappedComponent: C,
	): ComponentType<any> => // eslint-disable-line @typescript-eslint/no-explicit-any
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(props: any) => (
		<ScrollLongTaskAnalytics
			// @ts-expect-error Argument of type 'UIAnalyticsEvent' is not assignable to parameter of type 'InteractionMetricType'.
			analyticsEvent={PRODUCT_ANALYTICS_EVENT_NAMES.COMMIT_MODAL_SCROLLED}
		>
			{(handleScroll: () => void) => <WrappedComponent {...props} handleScroll={handleScroll} />}
		</ScrollLongTaskAnalytics>
	);
